import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import api from '../utils/api';

// Count-up animation hook
const useCountUp = (target, duration = 1800, start = false) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start || target == null) return;
    let raf;
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      // easeOutExpo
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setValue(Math.round(eased * target));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return value;
};

const Stat = ({ label, value, suffix = '', start }) => {
  const count = useCountUp(value, 1800, start);
  return (
    <div className="flex-1 text-center px-4">
      <p data-testid={`impact-stat-${label.toLowerCase().replace(/\s+/g, '-')}`} className="text-5xl md:text-7xl lg:text-8xl font-light text-white tracking-tight leading-none tabular-nums">
        {count.toLocaleString('en-IN')}{suffix}
      </p>
      <p className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-white/60 mt-4">{label}</p>
    </div>
  );
};

// Reusable impact tracker — hits /api/impact/stats on mount.
// `variant="dark"` (default) = dark green background for standalone section.
// `variant="light"` = cream card for inline use.
const ImpactTracker = ({ variant = 'dark', title = 'Impact, in real time' }) => {
  const [stats, setStats] = useState({ pots_sold: 0, women_empowered: 4 });
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  useEffect(() => {
    let mounted = true;
    api.get('/impact/stats')
      .then(r => { if (mounted) setStats(r.data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const isDark = variant === 'dark';

  return (
    <section
      ref={ref}
      data-testid="impact-tracker"
      className={`${isDark ? 'bg-[#641b4d]' : 'bg-[#F4F4F4] border-y border-[#E5E2DC]'} py-20 md:py-28 px-6`}
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#bc64aa] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#bc64aa]" />
            </span>
            <p className={`text-[10px] md:text-xs uppercase tracking-[0.3em] ${isDark ? 'text-[#bc64aa]' : 'text-[#bc64aa]'}`}>
              Live · Updated in real time
            </p>
          </div>
          <h2 className={`text-2xl md:text-4xl font-light tracking-tight ${isDark ? 'text-white' : 'text-[#641b4d]'}`}>
            {title}
          </h2>
        </motion.div>

        <div className={`grid grid-cols-2 divide-x ${isDark ? 'divide-white/15' : 'divide-[#E5E2DC]'}`}>
          {/* Stat wrapper clones children to pass `start` trigger */}
          <StatWrapper inView={inView} isDark={isDark} value={stats.pots_sold} label="Pots Sold" suffix="+" />
          <StatWrapper inView={inView} isDark={isDark} value={stats.women_empowered} label="Women Empowered" suffix="" />
        </div>
      </div>
    </section>
  );
};

// Small wrapper that handles color variants for the Stat
const StatWrapper = ({ inView, isDark, value, label, suffix }) => {
  const count = useCountUp(value, 1800, inView);
  return (
    <div className="flex-1 text-center px-2 md:px-4">
      <p
        data-testid={`impact-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
        className={`text-5xl md:text-7xl lg:text-8xl font-light tracking-tight leading-none tabular-nums ${isDark ? 'text-white' : 'text-[#641b4d]'}`}
      >
        {count.toLocaleString('en-IN')}{suffix}
      </p>
      <p className={`text-[10px] md:text-xs uppercase tracking-[0.3em] mt-4 ${isDark ? 'text-white/60' : 'text-[#7a5e75]'}`}>
        {label}
      </p>
    </div>
  );
};

export { Stat };
export default ImpactTracker;
